import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
  Paper,
  Stack,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { getMoveService } from "../../redux/action/move-service";
import Header from "../header/Header";
import { getCarrierByBusinessName } from "../../redux/action/carrier";
import { setTheme } from "../../redux/reducers/themeSlice";

const BookedMoveInfo = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { canonicalId } = useParams();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [moveData, setMoveData] = useState<any | null>(null);
  const { carrierCanonicalId } = useParams();
  useEffect(() => {
    if (canonicalId) {
      dispatch<any>(getMoveService(canonicalId))
        .then(unwrapResult)
        .then((res: any) => {
          setMoveData(res);
        })
        .catch((err: any) => {
          console.error("Failed to fetch move data:", err);
        });
    }
  }, []);

  const isValidColor = (color: string): boolean => {
    const colorRegex =
      /^#([0-9A-Fa-f]{3}){1,2}$|^(rgb|rgba|hsl|hsla)\([^\)]*\)$/;
    return colorRegex.test(color);
  };
  const { businessName } = useParams();
  const CarrierBusinessName = localStorage.getItem("businessName");
  useEffect(() => {
    if (CarrierBusinessName) {
      return;
    } else {
      if (businessName) {
        dispatch<any>(getCarrierByBusinessName(businessName))
          .then(unwrapResult)
          .then((res: any) => {
            localStorage.setItem("businessName", businessName);
            if (res.color_hex && isValidColor(res.color_hex)) {
              dispatch(
                setTheme({
                  primaryColor: res.color_hex,
                  secondaryColor: "#A8E6FF",
                })
              );
            }
          })
          .catch((err: any) => {
            console.error("Failed to fetch carrier data:", err);
          });
      }
    }
  }, [businessName, dispatch]);

  const getTotalPrice = () => {
    if (moveData.isPackingRequired === true) {
      const cost = Number(moveData.price) + Number(moveData.packingPrice);
      return cost;
    } else if (moveData.isPackingRequired === false) {
      return Number(moveData.price);
    }
  };
  return (
    <div>
      <Header />
      <Container
        sx={{
          width: "100%",
          maxWidth: "100%",
          height: isMobile ? "93vh" : "80vh",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
          padding: isMobile ? "20px" : "20px",
          background: "#f2f0f0",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          overflowY: "auto",
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          {/* Move Order Number (ID) */}
          {moveData && (
            <Typography
              fontWeight={600}
              fontSize={isMobile ? "14px" : "16px"}
              textAlign="center"
              color={theme.palette.primary.main}
              mb={2}
            >
              <strong>Move Order Number: </strong>
              {moveData.move_order_number}
            </Typography>
          )}

          <Typography
            fontWeight={700}
            fontSize={isMobile ? "16px" : "18px"}
            textAlign="center"
            mb={2}
          >
            Booked Move Information
          </Typography>
          <Divider sx={{ background: "#D3D3D3", mb: 2 }} />

          <Typography fontWeight={400} fontSize={16} textAlign="center" mb={1}>
            {moveData
              ? `THANK YOU FOR BOOKING YOUR MOVE WITH ${moveData?.carrier?.business_name?.toUpperCase()}`
              : ""}
          </Typography>
          <Typography
            fontWeight={400}
            fontSize={16}
            textAlign="center"
            color={theme.palette.primary.main}
            mb={2}
          ></Typography>

          <Divider sx={{ background: "#D3D3D3", mb: 2 }} />

          {/* Form Fields */}
          {moveData && (
            <>
              <Paper
                sx={{
                  padding: 3,
                  marginBottom: 3,
                  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                }}
              >
                <Box>
                  <Typography
                    fontWeight={600}
                    fontSize={18}
                    textAlign="left"
                    mb={1}
                  >
                    NAME
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={16}
                    textAlign="left"
                    mb={2}
                  >
                    {moveData.user.first_name} {moveData.user.last_name}
                  </Typography>
                </Box>

                <Divider sx={{ background: "#D3D3D3", mb: 2 }} />

                <Box>
                  <Typography
                    fontWeight={600}
                    fontSize={18}
                    textAlign="left"
                    mb={1}
                  >
                    PHONE NUMBER
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={16}
                    textAlign="left"
                    mb={2}
                  >
                    +1{moveData.user.phone_number}
                  </Typography>
                </Box>

                <Divider sx={{ background: "#D3D3D3", mb: 2 }} />

                <Box>
                  <Typography
                    fontWeight={600}
                    fontSize={18}
                    textAlign="left"
                    mb={1}
                  >
                    MOVE TYPE
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={16}
                    textAlign="left"
                    mb={2}
                  >
                    {Number(moveData.distance) <= 200
                      ? "Local"
                      : "Long Distance"}
                  </Typography>
                </Box>

                <Divider sx={{ background: "#D3D3D3", mb: 2 }} />

                <Box>
                  <Typography
                    fontWeight={600}
                    fontSize={18}
                    textAlign="left"
                    mb={1}
                  >
                    Date of Move
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={16}
                    textAlign="left"
                    mb={2}
                  >
                    {new Date(moveData.moving_date).toLocaleDateString()}
                  </Typography>
                </Box>

                <Divider sx={{ background: "#D3D3D3", mb: 2 }} />

                <Box>
                  <Typography
                    fontWeight={600}
                    fontSize={18}
                    textAlign="left"
                    mb={1}
                  >
                    Pickup Location
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={16}
                    textAlign="left"
                    mb={2}
                  >
                    {moveData.moving_from}
                  </Typography>
                </Box>

                <Divider sx={{ background: "#D3D3D3", mb: 2 }} />

                <Box>
                  <Typography
                    fontWeight={600}
                    fontSize={18}
                    textAlign="left"
                    mb={1}
                  >
                    Delivery Location
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={16}
                    textAlign="left"
                    mb={2}
                  >
                    {moveData.moving_to}
                  </Typography>
                </Box>

                <Divider sx={{ background: "#D3D3D3", mb: 2 }} />

                <Box>
                  <Typography
                    fontWeight={600}
                    fontSize={18}
                    textAlign="left"
                    mb={1}
                  >
                    Move Distance (how many miles)
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={16}
                    textAlign="left"
                    mb={2}
                  >
                    {moveData.distance} miles
                  </Typography>
                </Box>
                <Divider sx={{ background: "#D3D3D3", mb: 2 }} />

                <Box>
                  <Typography
                    fontWeight={600}
                    fontSize={18}
                    textAlign="left"
                    mb={1}
                  >
                    Deposit Paid:
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={16}
                    textAlign="left"
                    mb={2}
                  >
                    ${moveData.initial_deposit}
                  </Typography>
                </Box>
                <Divider sx={{ background: "#D3D3D3", mb: 2 }} />

                <Box>
                  <Typography
                    fontWeight={600}
                    fontSize={18}
                    textAlign="left"
                    mb={1}
                  >
                    Total Cost of the Move:
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={16}
                    textAlign="left"
                    mb={2}
                  >
                    ${getTotalPrice()}
                  </Typography>
                </Box>
              </Paper>
            </>
          )}
          <Stack my={4} alignItems={"center"} justifyContent={"center"}>
            <Button
              onClick={() => {
                setIsLoading(true);
                setLoadingMessage("Redirecting to Dashboard...");
                setTimeout(() => {
                  navigate(
                    `/${businessName}/${carrierCanonicalId}/consumer-dashboard/${moveData?.user?.canonical_id}`
                  );
                }, 1500);
              }}
              variant="contained"
            >
              Proceed to Dashboard
            </Button>
          </Stack>
          {isLoading && (
            <Backdrop
              sx={{
                color: `${theme.palette.primary}`,
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backdropFilter: "blur(5px)",
                backgroundColor: "rgba(0,0,0,0.2)",
              }}
              open={isLoading}
            >
              <CircularProgress size={50} color="inherit" />
              <Typography
                variant="h6"
                sx={{ mt: 2, color: `${theme.palette.primary}`, marginLeft: 1 }}
              >
                {loadingMessage}
              </Typography>
            </Backdrop>
          )}
          <Typography fontWeight={700} fontSize={18} textAlign="center" mb={1}>
            For any queries please reach out to us at{" "}
            {`+1 ${moveData?.carrier?.customer_service_phone}`}
          </Typography>
          <Typography
            fontWeight={700}
            fontSize={18}
            textAlign="center"
            mb={3}
            color={theme.palette.primary.main}
          >
            {/* {moveData.phone_number } */}
          </Typography>
        </Box>
      </Container>
    </div>
  );
};

export default BookedMoveInfo;
